import { ExperimentResult } from './experimentResults';
import { SlideTagAssignment } from './slideTagAssignment';

export interface SlideCaseAssignmentItem {
  slide: Slide;
  isAssigned: boolean;
  assignments: { caseId: number; caseLabel: string; studyId: string }[];
  hasRestrictedAssignments: boolean;
}

export interface SlideCaseAssignmentOptionsResponse {
  items: SlideCaseAssignmentItem[];
  nextCursor: string | null;
}

export interface Slide {
  id: string;
  studyId?: string;
  sourceStudyId?: string;
  labId?: string;
  sourceLabName?: string;
  scanDate?: string;
  format?: string;
  scannerId?: string;
  scannerModel?: string;
  scannerManufacturer?: string;
  maxResolution?: number;
  sizeRows?: number;
  sizeCols?: number;
  objectiveMagnification?: number;
  numLayers?: number;
  createdAt?: string;
  originalFileName?: string;
  sourceSlideUri?: string | null;
  biopsySiteId?: number | string; // The string is for the case manifest where we display the raw value if the biopsy site is not found
  stainingType?: string;
  stainingSubType?: string;
  labInternalId?: string;
  biopsyType?: string | null;
  procedureType?: string | null;
  preservationMethod?: string | null;
  registrations?: Registration[] | null;
  qcFailed?: boolean;
  channels?: Record<string, SlideChannel>;
  channelMarkerTypes?: Record<string, string> | null;
  channelMarkersQc?: Record<string, string> | null;
  dnaChannelId?: string | null;
  metastasis?: boolean;
  tsmAreaUm2?: number | null;
  experimentResults?: ExperimentResult[];
  qcLabels?: SlideQcLabel[];
  excluded?: boolean;
  slideTagAssignments?: SlideTagAssignment[];
  cancerTypeId?: number; // This field is only used in ingest, please refer to the cancerType field in the Procedure table
  negativeControl?: boolean;
  positiveControl?: boolean;
  encoding?: 'rgb' | 'uint8' | 'uint16' | 'float';
}

export const slidesEditableKeys: string[] = [
  'biopsySiteId',
  'stainingType',
  'biopsyType',
  'procedureType',
  'preservationMethod',
  'metastasis',
  'negativeControl',
  'positiveControl',
  'encoding',
];

export interface SlideQcLabel {
  text: string;
  errorCalculatingLabel: boolean;
  errorMessage?: string;
}

export interface SlideChannel {
  name?: string;
  markerType?: string;
  histogram?: number[];
  lut?: number[];
  color?: string;
  // Only RNA channels carry this; DNA/mIF come from channel_marker_types and the stain table.
  channelModality?: 'rna';
}

export interface QcResult {
  qcKey: string;
  isPassed: boolean;
}

export interface Channel {
  key: string;
  value: number | string;
  url?: string;
  heatmapUrl?: string;
}

export interface Registration {
  slideId: string;
  registrationSlideId: string;
  angle?: number;
  zoomRatio?: number;
  points1?: any;
  points2?: any;
  approved: boolean;
  approvedDate?: string;
  orchestrationId?: string;
  studyId?: string;
  createdAt?: string;
  artifactUrl?: string;
}

export interface SlidePrefetchData
  extends Pick<
    Slide,
    'id' | 'labId' | 'sizeCols' | 'sizeRows' | 'maxResolution' | 'originalFileName' | 'channelMarkerTypes'
  > {
  caseId?: number;
  caseLabel?: string;
  slideId: string;
  slideFormat: string;
  stainTypeId?: string;
  numChannels?: number;
}
